import { db } from "../db/index.js";
import { syncStatus, resources as resourcesStore } from "../stores.js";
import { triggerSync } from "./registerSW.js";
import axios, { axiosWithCsrfRetry } from "@edu-quizz/shared/lib/axios";
import { cacheAssets } from "./assetCaching.js";

/**
 * API Base URL - adjust this to match your Laravel backend
 */
const API_BASE_URL = "/api";

/**
 * Sync manager for handling data synchronization between local DB and server
 */
export class SyncManager {
    constructor() {
        this.isSyncing = false;
        this.syncInterval = null;
    }

    /**
     * Download resources from server and save to local DB
     */
    async downloadResources() {
        try {
            console.log("Downloading offline resources from server...");

            const response = await axios.get(
                `${API_BASE_URL}/v1/student/offline-resources`,
            );
            const data = response.data;

            // Save to IndexedDB
            await db.saveOfflineResources(data);

            console.log("Offline resources downloaded and saved successfully");
            return true;
        } catch (error) {
            console.error("Failed to download offline resources:", error);
            throw error;
        }
    }

    /**
     * Download flashcard reviews from server and save to local DB
     */
    async downloadFlashcardReviews() {
        try {
            console.log("Downloading flashcard reviews from server...");

            const response = await axios.get(
                `${API_BASE_URL}/v1/flashcard-reviews`,
            );
            const data = response.data;

            // Save reviews to IndexedDB
            if (data.reviews && data.reviews.length > 0) {
                for (const review of data.reviews) {
                    // Check if review already exists locally
                    const existingReview = await db.flashcardReviews.get(
                        review.uuid,
                    );
                    if (!existingReview) {
                        await db.flashcardReviews.add(review);
                    }
                }
                console.log(
                    `Downloaded ${data.reviews.length} flashcard reviews`,
                );
            }

            return true;
        } catch (error) {
            console.error("Failed to download flashcard reviews:", error);
            throw error;
        }
    }

    /**
     * Download quiz answers from server and save to local DB
     */
    async downloadQuizAnswers() {
        try {
            console.log("Downloading quiz answers from server...");

            const response = await axios.get(`${API_BASE_URL}/v1/quiz-answers`);
            const data = response.data;

            // Save answers to IndexedDB
            if (data.answers && data.answers.length > 0) {
                for (const answer of data.answers) {
                    // Check if answer already exists locally
                    const existingAnswer = await db.quizAnswers.get(
                        answer.uuid,
                    );
                    if (!existingAnswer) {
                        await db.quizAnswers.add(answer);
                    }
                }
                console.log(`Downloaded ${data.answers.length} quiz answers`);
            }

            return true;
        } catch (error) {
            console.error("Failed to download quiz answers:", error);
            throw error;
        }
    }

    /**
     * Upload local changes to server
     */
    async uploadChanges() {
        try {
            console.log("Uploading local changes to server...");

            const pendingItems = await db.getPendingSyncItems();

            if (pendingItems.length === 0) {
                console.log("No pending changes to upload");
                return true;
            }

            console.log(`Uploading ${pendingItems.length} pending items...`);

            // Process items in chronological order, batching compatible actions
            let flashcardReviewsBatch = [];
            let quizAnswersBatch = [];

            for (const item of pendingItems) {
                // Special actions: flush current batches then handle the action
                if (item.action === "reset_deck" || item.action === "reset_quiz") {
                    // Send accumulated batches before the reset
                    await this.flushBatches(flashcardReviewsBatch, quizAnswersBatch);
                    flashcardReviewsBatch = [];
                    quizAnswersBatch = [];

                    // Handle the special action
                    await this.handleSpecialAction(item);
                    continue;
                }

                // Flashcard reviews: accumulate in batch
                if (
                    item.resourceType === "flashcard_deck" &&
                    item.targetLocalTableName === "flashcardReviews" &&
                    (item.action === "create" || item.action === "update")
                ) {
                    flashcardReviewsBatch.push(item);
                    continue;
                }

                // Quiz answers: accumulate in batch
                if (
                    item.resourceType === "quiz" &&
                    item.targetLocalTableName === "quizAnswers" &&
                    (item.action === "create" || item.action === "update")
                ) {
                    quizAnswersBatch.push(item);
                    continue;
                }

                // Other items: flush batches then handle individually
                await this.flushBatches(flashcardReviewsBatch, quizAnswersBatch);
                flashcardReviewsBatch = [];
                quizAnswersBatch = [];

                await this.handleOtherItem(item);
            }

            // Send remaining batches
            await this.flushBatches(flashcardReviewsBatch, quizAnswersBatch);

            console.log("Local changes uploaded successfully");
            return true;
        } catch (error) {
            console.error("Failed to upload changes:", error);
            throw error;
        }
    }

    /**
     * Flush accumulated batches
     */
    async flushBatches(flashcardReviewsBatch, quizAnswersBatch) {
        if (flashcardReviewsBatch.length > 0) {
            try {
                await this.uploadFlashcardReviewsBatch(flashcardReviewsBatch);
            } catch (error) {
                console.error("Failed to upload flashcard reviews batch:", error);
                for (const item of flashcardReviewsBatch) {
                    await db.incrementSyncRetry(item.id);
                }
            }
        }

        if (quizAnswersBatch.length > 0) {
            try {
                await this.uploadQuizAnswersBatch(quizAnswersBatch);
            } catch (error) {
                console.error("Failed to upload quiz answers batch:", error);
                for (const item of quizAnswersBatch) {
                    await db.incrementSyncRetry(item.id);
                }
            }
        }
    }

    /**
     * Handle special actions (reset_deck, reset_quiz)
     */
    async handleSpecialAction(item) {
        try {
            if (item.action === "reset_deck") {
                await axiosWithCsrfRetry(() =>
                    axios.post(
                        `${API_BASE_URL}/v1/flashcard-reviews/sync`,
                        {
                            action: "reset_deck",
                            resource_id: item.resourceId,
                        },
                    ),
                );
            } else if (item.action === "reset_quiz") {
                await axiosWithCsrfRetry(() =>
                    axios.post(`${API_BASE_URL}/v1/quiz-answers/sync`, {
                        action: "reset_quiz",
                        resource_id: item.resourceId,
                    }),
                );
            }
            await db.removeFromSyncQueue(item.id);
        } catch (error) {
            console.error(
                `Failed to upload special action ${item.action}:`,
                error,
            );
            await db.incrementSyncRetry(item.id);
        }
    }

    /**
     * Handle other items individually
     */
    async handleOtherItem(item) {
        try {
            await this.uploadSyncItem(item);
            await db.removeFromSyncQueue(item.id);
        } catch (error) {
            console.error(`Failed to upload item ${item.id}:`, error);
            await db.incrementSyncRetry(item.id);

            if (item.retryCount >= 5) {
                console.warn(
                    `Item ${item.id} has failed too many times, skipping`,
                );
            }
        }
    }

    /**
     * Upload flashcard reviews in batch
     */
    async uploadFlashcardReviewsBatch(reviewItems) {
        console.log(
            `Uploading ${reviewItems.length} flashcard reviews in batch...`,
        );

        // Extract review data from sync items, including the sync queue UUID
        const reviews = reviewItems.map((item) => ({
            ...item.data,
            sync_queue_uuid: item.uuid,
        }));

        // Send batch request with automatic CSRF retry
        const response = await axiosWithCsrfRetry(() =>
            axios.post(`${API_BASE_URL}/v1/flashcard-reviews/sync`, {
                reviews,
            }),
        );

        const result = response.data;

        // Process results
        if (result.success && result.success.length > 0) {
            console.log(`Successfully synced ${result.success.length} reviews`);
            // Remove successful items from sync queue using sync_queue_uuid
            for (const successItem of result.success) {
                const syncItem = reviewItems.find(
                    (item) => item.uuid === successItem.sync_queue_uuid,
                );
                if (syncItem) {
                    await db.removeFromSyncQueue(syncItem.id);
                }
            }
        }

        if (result.failed && result.failed.length > 0) {
            console.error(`Failed to sync ${result.failed.length} reviews`);
            // Log failed items and increment retry count
            for (const failedItem of result.failed) {
                console.error(
                    `Failed review ${failedItem.uuid} (sync_queue_uuid: ${failedItem.sync_queue_uuid}):`,
                    failedItem.error,
                );
                // Find the sync item and increment retry count (with limit check)
                const syncItem = reviewItems.find(
                    (item) => item.uuid === failedItem.sync_queue_uuid,
                );
                if (syncItem) {
                    await db.incrementSyncRetryWithLimitRetry(syncItem.id);
                }
            }
        }

        return result;
    }

    /**
     * Upload quiz answers in batch
     */
    async uploadQuizAnswersBatch(answerItems) {
        console.log(`Uploading ${answerItems.length} quiz answers in batch...`);

        // Extract answer data from sync items, including the sync queue UUID
        const answers = answerItems.map((item) => ({
            ...item.data,
            sync_queue_uuid: item.uuid,
        }));

        // Send batch request with automatic CSRF retry
        const response = await axiosWithCsrfRetry(() =>
            axios.post(`${API_BASE_URL}/v1/quiz-answers/sync`, { answers }),
        );

        const result = response.data;

        // Process results
        if (result.success && result.success.length > 0) {
            console.log(
                `Successfully synced ${result.success.length} quiz answers`,
            );
            // Remove successful items from sync queue using sync_queue_uuid
            for (const successItem of result.success) {
                const syncItem = answerItems.find(
                    (item) => item.uuid === successItem.sync_queue_uuid,
                );
                if (syncItem) {
                    await db.removeFromSyncQueue(syncItem.id);
                }
            }
        }

        if (result.failed && result.failed.length > 0) {
            console.error(
                `Failed to sync ${result.failed.length} quiz answers`,
            );
            // Log failed items and increment retry count
            for (const failedItem of result.failed) {
                console.error(
                    `Failed quiz answer ${failedItem.uuid} (sync_queue_uuid: ${failedItem.sync_queue_uuid}):`,
                    failedItem.error,
                );
                // Find the sync item and increment retry count (with limit check)
                const syncItem = answerItems.find(
                    (item) => item.uuid === failedItem.sync_queue_uuid,
                );
                if (syncItem) {
                    await db.incrementSyncRetryWithLimitRetry(syncItem.id);
                }
            }
        }

        return result;
    }

    /**
     * Upload a single sync item to server
     */
    async uploadSyncItem(item) {
        let url = `${API_BASE_URL}/${item.resourceType}`;

        let response;

        // Determine HTTP method based on action
        if (item.action === "update") {
            url += `/${item.resourceId}`;
            response = await axiosWithCsrfRetry(() =>
                axios.put(url, item.data),
            );
        } else if (item.action === "delete") {
            url += `/${item.resourceId}`;
            response = await axiosWithCsrfRetry(() => axios.delete(url));
        } else {
            response = await axiosWithCsrfRetry(() =>
                axios.post(url, item.data),
            );
        }

        return response.data;
    }

    /**
     * Perform full sync (download resources and upload changes)
     */
    async fullSync() {
        if (this.isSyncing) {
            console.log("Sync already in progress");
            return false;
        }

        this.isSyncing = true;
        syncStatus.setSyncing(true);

        // Create a sync attempt record
        const syncAttemptId = await db.addSyncAttempt('in_progress');

        try {
            // First, upload any pending local changes
            await this.uploadChanges();

            // Then, download latest resources from server
            await this.downloadResources();

            // Download flashcard reviews
            await this.downloadFlashcardReviews();

            // Download quiz answers
            await this.downloadQuizAnswers();

            // Cache assets for offline use (PWA only)
            try {
                const allAssets = await db.assets.toArray();
                const cacheResult = await cacheAssets(allAssets, (current, total) => {
                    syncStatus.setAssetCachingProgress(current, total);
                });
                console.log(`Assets cached: ${cacheResult.success} success, ${cacheResult.failed} failed`);
            } catch (error) {
                console.error("Asset caching failed:", error);
                // Don't fail the entire sync if asset caching fails
            }

            // Cleanup assets marked for deletion
            try {
                const assetsToDelete = await db.getAssetsToDelete();

                if (assetsToDelete.length > 0) {
                    console.log(`Cleaning up ${assetsToDelete.length} marked assets...`);

                    // Delete assets from PWA cache
                    if ('caches' in window) {
                        const cache = await caches.open('assets-cache-v1.0.1');
                        for (const asset of assetsToDelete) {
                            if (asset.urlCachePWA) {
                                await cache.delete(asset.urlCachePWA).catch(err =>
                                    console.warn(`Failed to delete ${asset.urlCachePWA} from cache:`, err)
                                );
                            }
                        }
                    }

                    // Remove marked assets from database
                    const deletedCount = await db.cleanupMarkedAssets();
                    console.log(`Cleaned up ${deletedCount} assets from database and cache`);
                }
            } catch (error) {
                console.error("Asset cleanup failed:", error);
                // Don't fail the entire sync if asset cleanup fails
            }

            // Update stores
            await resourcesStore.load();
            await syncStatus.updatePendingCount();

            // Update last sync time in DB
            const auth = await db.getAuth();
            if (auth) {
                await db.auth.update(1, {
                    lastSync: new Date().toISOString(),
                });
            }

            // Update sync attempt to success
            await db.updateSyncAttempt(syncAttemptId, 'success');

            syncStatus.setSyncCompleted();
            console.log("Full sync completed successfully");
            return true;
        } catch (error) {
            console.error("Full sync failed:", error);

            // Update sync attempt to error with error message
            await db.updateSyncAttempt(syncAttemptId, 'error', error.message);

            syncStatus.setSyncError(error.message);
            return false;
        } finally {
            this.isSyncing = false;
        }
    }

    /**
     * Start automatic sync interval
     */
    startAutoSync(intervalMinutes = 3) {
        if (this.syncInterval) {
            console.log("Auto-sync already running");
            return;
        }

        console.log(`Starting auto-sync every ${intervalMinutes} minutes`);

        this.syncInterval = setInterval(
            async () => {
                // Only sync if online
                if (navigator.onLine) {
                    console.log("Auto-sync triggered");
                    await this.fullSync();
                }
            },
            intervalMinutes * 60 * 1000,
        );
    }

    /**
     * Stop automatic sync
     */
    stopAutoSync() {
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
            this.syncInterval = null;
            console.log("Auto-sync stopped");
        }
    }

    /**
     * Trigger background sync via service worker
     */
    triggerBackgroundSync() {
        triggerSync();
    }
}

// Create and export singleton instance
export const syncManager = new SyncManager();

/**
 * Initialize sync when coming online
 */
if (typeof window !== "undefined") {
    window.addEventListener("online", async () => {
        console.log("Network online, triggering sync...");

        // Get auth
        const auth = await db.getAuth();
        if (auth) {
            // Use background sync if supported
            syncManager.triggerBackgroundSync();

            // Also trigger immediate sync
            await syncManager.fullSync();
        }
    });
}

/**
 * Get schools hierarchy from local database
 */
export async function getSchoolsHierarchy() {
    try {
        const hierarchy = await db.getSchoolsHierarchy();
        return {
            success: true,
            data: hierarchy,
        };
    } catch (error) {
        console.error("Failed to get schools hierarchy:", error);
        return {
            success: false,
            error: error.message,
        };
    }
}

/**
 * Helper function to authenticate with code
 */
export async function authenticateWithCode(code, remember = false) {
    try {
        const response = await axiosWithCsrfRetry(() =>
            axios.post(`${API_BASE_URL}/auth/login-with-code`, {
                code,
                remember,
            }),
        );

        const data = response.data;

        // Save auth to database (no token needed, using cookie-based auth)
        console.log("authenticateWithCode", data, data.user);
        await db.saveAuth(code, data.user, remember);

        // Perform initial sync (includes downloading offline resources)
        await syncManager.fullSync();

        // Start auto-sync
        syncManager.startAutoSync();

        return {
            success: true,
            message: data.message,
            user: data.user,
        };
    } catch (error) {
        console.error("Authentication failed:", error);
        return {
            success: false,
            error: error.message,
        };
    }
}
